import { FC } from "react";

import { Box } from "@mui/material";
import { EVENT_REMINDER_MAX_OFFSET, ScheduleOffsetUnit } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { AutomatedCommunicationCard } from ".././components/AutomatedCommunicationCard";
import { SendAutomatedEmailTestButton } from "../components/SendAutomatedEmailTestButton";
import { SmsOptInConfirmation } from "../helpers/useSmsOptInConfirmation";
import { AutomatedCommunicationsFormData, RowKey } from "../types";

import { Translate, useTranslate } from "@/common/hooks/useTranslate";

interface AuctionReminderCardProps {
  num: 1 | 2;
  openRow: RowKey | null;
  onToggleOpen: (row: RowKey) => void;
  isSmsEnabled: boolean;
  smsOptIn: SmsOptInConfirmation;
  defaultContent: string;
}

export const AuctionReminderCard: FC<AuctionReminderCardProps> = ({
  num,
  openRow,
  onToggleOpen,
  isSmsEnabled,
  smsOptIn,
  defaultContent,
}) => {
  const { t } = useTranslate();
  const form = useFormContext<AutomatedCommunicationsFormData>();
  const fieldKey = num === 1 ? ("reminder1" as const) : ("reminder2" as const);
  const rowKey: RowKey = num === 1 ? "reminder-1" : "reminder-2";

  const emailEnabled = useWatch({ control: form.control, name: `${fieldKey}.emailEnabled` });
  const smsEnabled = useWatch({ control: form.control, name: `${fieldKey}.smsEnabled` });
  const offset = useWatch({ control: form.control, name: `${fieldKey}.offset` });
  const offsetUnit = useWatch({ control: form.control, name: `${fieldKey}.offsetUnit` });

  const rowEnabled = emailEnabled || smsEnabled;
  const isEmailActive = !isSmsEnabled || emailEnabled;

  const setRowEnabled = (next: boolean) => {
    form.setValue(`${fieldKey}.emailEnabled`, next, { shouldDirty: true });
    form.setValue(`${fieldKey}.smsEnabled`, false, { shouldDirty: true });
  };

  return (
    <AutomatedCommunicationCard<AutomatedCommunicationsFormData>
      label={t("dashboard", `ticketing.automatedCommunications.row.reminder${num}.label`)}
      control={form.control}
      errors={form.formState.errors}
      dataEmailType={rowKey}
      fields={{ subject: `${fieldKey}.subject`, content: `${fieldKey}.content` }}
      enabled={rowEnabled}
      onEnabledChange={setRowEnabled}
      chip={{ label: formatAuctionReminderTrigger(t, offset, offsetUnit), showWhen: "enabled" }}
      collapse={{ isOpen: openRow === rowKey, onToggle: () => onToggleOpen(rowKey) }}
      channelIcons={isSmsEnabled ? { email: emailEnabled, sms: smsEnabled } : undefined}
      channelSelectors={
        isSmsEnabled
          ? {
              email: {
                checked: emailEnabled,
                onChange: (value) => form.setValue(`${fieldKey}.emailEnabled`, value, { shouldDirty: true }),
              },
              sms: {
                checked: smsEnabled,
                onChange: smsOptIn.guardSetSmsEnabled((value) =>
                  form.setValue(`${fieldKey}.smsEnabled`, value, { shouldDirty: true })
                ),
              },
            }
          : undefined
      }
      offset={{
        name: `${fieldKey}.offset`,
        suffixLabel: t("dashboard", "ticketing.automatedCommunications.reminder.beforeAuctionClose"),
        unitField: {
          name: `${fieldKey}.offsetUnit`,
          options: [
            {
              value: ScheduleOffsetUnit.Days,
              label: t("dashboard", "ticketing.automatedCommunications.reminder.unit.days"),
            },
            {
              value: ScheduleOffsetUnit.Hours,
              label: t("dashboard", "ticketing.automatedCommunications.reminder.unit.hours"),
            },
          ],
        },
        maxOffset: EVENT_REMINDER_MAX_OFFSET,
      }}
      body={{ defaultValue: defaultContent, attachmentFieldName: `${fieldKey}.attachment`, readyToMount: true }}
      dataTestPrefix={rowKey}
      footnote={
        isEmailActive ? (
          <Box marginTop={2} display="flex" justifyContent="flex-start">
            <SendAutomatedEmailTestButton<AutomatedCommunicationsFormData>
              emailType="ticketing-reminder"
              subjectFieldName={`${fieldKey}.subject`}
              contentFieldName={`${fieldKey}.content`}
              attachmentFieldName={`${fieldKey}.attachment`}
            />
          </Box>
        ) : undefined
      }
    />
  );
};

const formatAuctionReminderTrigger = (
  t: Translate,
  offset: number | undefined,
  unit: ScheduleOffsetUnit | undefined
): string => {
  const count = offset ?? 1;
  const key =
    unit === ScheduleOffsetUnit.Hours
      ? "ticketing.automatedCommunications.trigger.hoursBeforeClose"
      : "ticketing.automatedCommunications.trigger.daysBeforeClose";
  return t("dashboard", key, { count });
};
